import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import './ampol-cash-orders.scss';
import { Button, ButtonColor, ButtonVariant, ButtonType, DataTable, Form } from '@ampol/design-system';
import { currencyFormat } from '../../../utilities/core-utilities';
import { DATE_FORMAT, DATE_FORMAT_HYPHEN, cashOrderStatuses } from 'utilities/constants';

import PageHeader from '../../../components/layout/page-header/page-header';
import ContentControls from '../../../components/content-controls/content-controls';
import FilterControl from '../../../components/filter-control/filter-control';
import DeprecatedIcon from '../../../assets/svg-icons';
import AmpolCashOrderDetailsModal from '../modals/ampol-cash-order-details-modal/ampol-cash-order-details-modal';
import AmpolCashCardDetailsModal from '../modals/ampol-cash-card-details-modal/ampol-cash-card-details-modal';
import AmpolCashOrderModal from '../modals/ampol-cash-order-modal/ampol-cash-order-modal';
import Paginator from '../../../components/pagination/pagination';
import Status from '../../../components/status/status';
import Env from '../../../Env';
import dayjs from 'dayjs';
import SuffixWrapper from 'components/suffix-wrapper/suffix-wrapper';
import Validation from 'constants/validation-messages';
import { selectAmpolCashOrdersPageContext } from '../../../selectors/ampolCashOrdersSelectors';

// Custom Hooks
import { useAmpolCashFilters } from './hooks/useAmpolCashFilters';
import { useAmpolCashModals } from './hooks/useAmpolCashModals';
import { useAmpolCashData } from './hooks/useAmpolCashData';

/**
 * Ampol Cash Orders
 *
 * @returns JSX Element
 */
const AmpolCashOrders = () => {
  const dispatch = useDispatch();

  const context = useSelector(selectAmpolCashOrdersPageContext);

  const {
    // User Store
    loggedInUser,
    assignedAccountNumber,
    assignedGrandfatherClientNumber,
    grandparentAccountLoading,
    grandparentAccountError,

    // Cash Store - Order List Data
    cashOrdersList,
    cashOrdersListLoading,
    cashOrdersListError,
    cashOrdersListResultSize,
    cashOrderCardDetails,
  } = context;

  const isAdminPortal = Env.isAdminPortal();

  // Selected order state (shared between hooks)
  const [selectedOrder, setSelectedOrder] = useState<any>(null);

  // Hook 1: Filter, search, sorting, pagination logic (~250 lines extracted)
  const filters = useAmpolCashFilters(loggedInUser, assignedAccountNumber);

  // Hook 2: Modal management and card selection (~200 lines extracted)
  const modals = useAmpolCashModals({
    context,
    selectedOrder,
    setSelectedOrder,
    AmpolCashOrderDetailsModal,
    AmpolCashCardDetailsModal,
  });

  // Hook 3: Data lifecycle and API calls (~250 lines extracted)
  const data = useAmpolCashData({
    context,
    filters,
    modals: {
      selectedOrder,
      setSelectedOrder,
      selectedCardDetails: modals.selectedCardDetails,
      setSelectedCardDetails: modals.setSelectedCardDetails,
      setIsCardDetailsClicked: modals.setIsCardDetailsClicked,
    },
  });

  // Component now focuses on composition and rendering
  return (
    <>
      {modals.showOrderModal && (
        <AmpolCashOrderModal
          onClose={() => modals.setShowOrderModal(false)}
          onCashOrderSubmitted={data.handleCashOrderSubmitted}
        />
      )}

      <PageHeader
        title='AmpolCash'
        showHeaderAccountSelector={isAdminPortal}
        allowCashAccountsOnly={true}
        showButton={true}
        buttonLabel='Order'
        onButtonClick={modals.handleOrderClick}
        isButtonInactive={!assignedGrandfatherClientNumber || grandparentAccountLoading || grandparentAccountError}
      />

      {assignedGrandfatherClientNumber && (
        <>
          <FilterControl
            showSearch={true}
            handleSearch={filters.handleSearch}
            searchSchema={filters.searchSchema}
            defaultSearchValue={filters.searchTerm}
            searchPlaceholder={'Search card number'}
            showFilterControl={true}
            filterToggle={filters.onToggleFilter}
            filterDisplay={filters.filterBarEnabled}
            filterApplied={filters.filterApplied}
          >
            <div className='ampol-cash-orders__filter-form'>
              <Form
                id={'ampol-cash-orders-filters-form'}
                schema={filters.filterFormSchema}
                onSubmit={filters.handleFilterSubmit}
                defaultValues={{ ...filters.cashOrdersQueryParams.filterValues }}
                onChange={filters.handleFilterChange}
              >
                <div className='ampol-cash-orders__filter-params'>
                  <div className='ampol-cash-orders__filter-params__grid-subtitle-1'>Issue date (range)</div>
                  <div className='ampol-cash-orders__filter-params__grid-span2'>
                    <div className='ampol-cash-orders__filter-input-container'>
                      <Form.DatePicker
                        className='ampol-cash-orders__filter-input'
                        placeholder='(dd/mm/yyyy)'
                        name='dateFrom'
                        label='Issue date from'
                        inputSuffix={
                          <SuffixWrapper>
                            <DeprecatedIcon iconName='calender' />
                          </SuffixWrapper>
                        }
                      />
                    </div>
                    <div className='ampol-cash-orders__filter-input-joiner' />
                    <div className='ampol-cash-orders__filter-input-container'>
                      <Form.DatePicker
                        className='ampol-cash-orders__filter-input'
                        placeholder='(dd/mm/yyyy)'
                        name='dateTo'
                        label='Issue date to'
                        labelType='hidden'
                        inputSuffix={
                          <SuffixWrapper>
                            <DeprecatedIcon iconName='calender' />
                          </SuffixWrapper>
                        }
                      />
                    </div>
                  </div>
                  <div className='ampol-cash-orders__filter-params__grid-subtitle-2'>Order value (range)</div>
                  <div className='ampol-cash-orders__filter-params__grid-span2'>
                    <div className='ampol-cash-orders__filter-input-container ampol-cash-orders'>
                      <Form.Input
                        className='ampol-cash-orders__filter-input'
                        label={'Order value from'}
                        name={'orderValueFrom'}
                      />
                    </div>
                    <div className='ampol-cash-orders__filter-input-joiner' />
                    <div className='ampol-cash-orders__filter-input-container'>
                      <Form.Input
                        className='ampol-cash-orders__filter-input'
                        label={'Order value to'}
                        name={'orderValueTo'}
                      />
                    </div>
                  </div>
                  <div className='ampol-cash-orders__filter-input-container'>
                    <Form.Input label={'Order number'} name={'orderNumber'} />
                  </div>
                  <div className='ampol-cash-orders__filter-input-container ampol-cash-orders'>
                    <Form.DropdownSearch
                      label={'Order status'}
                      name={'orderStatus'}
                      type={'dropdown'}
                      data={cashOrderStatuses}
                      allowClear={false}
                    />
                  </div>
                  <div className='ampol-cash-orders__filter-mobile-datepicker-padding' />
                  <div className='ampol-cash-orders__filter-button-container'>
                    <Button
                      variant={ButtonVariant.Secondary}
                      color={ButtonColor.Blue}
                      label='Clear All'
                      type={ButtonType.Button}
                      onClick={filters.handleClearFilters}
                    />
                    <Button
                      variant={ButtonVariant.Primary}
                      color={ButtonColor.Blue}
                      label='Filter'
                      type={ButtonType.Submit}
                    />
                  </div>
                </div>
              </Form>
            </div>
          </FilterControl>

          <ContentControls
            showPageControls={true}
            showSearch={false}
            handleSearch={filters.handleSearch}
            searchSchema={filters.searchSchema}
            defaultSearchValue={filters.searchTerm}
            pageNumber={filters.cashOrdersQueryParams.filterValues.pageNumber}
            totalRecords={cashOrdersListResultSize}
            itemCount={cashOrdersListResultSize}
            onPageChange={filters.handlePageNumberChange}
            showFilterControl={false}
            filterToggle={filters.onToggleFilter}
            filterDisplay={filters.filterBarEnabled}
            filterApplied={filters.filterApplied}
          />
        </>
      )}

      <div className={'ampol-cash-orders__table-container'}>
        {cashOrderCardDetails && (
          <DataTable
            caption={'Ampol Cash Card Details'}
            columns={[
              {
                header: 'Card Number',
                accessorKey: 'cardNumber',
              },
              {
                header: 'Denomination',
                accessorFn: (x) => currencyFormat(x.cardValue),
              },
              {
                header: 'Type',
                accessorKey: 'mediaType',
              },
              {
                header: 'Status',
                accessorFn: (x) => <Status value={x.status} />,
              },
            ]}
            columnTemplate={'1fr 1fr 1fr 1fr'}
            data={data.cardDetailsTableData}
            isLoading={cashOrdersListLoading}
            loadingRowCount={1}
            tableVariant='list'
            onRowClick={() => {
              modals.setSelectedCardDetails({ ...cashOrderCardDetails });
              setSelectedOrder(cashOrdersList[0]);
              modals.setIsCardDetailsClicked(true);
            }}
          />
        )}
      </div>

      <div className={'ampol-cash-orders__table-container'}>
        <DataTable
          caption={'Ampol Cash Orders'}
          columns={[
            {
              header: 'Date',
              id: 'orderDate',
              accessorFn: (x) => dayjs(x.orderDate, DATE_FORMAT_HYPHEN).format(DATE_FORMAT),
              defaultSorted: 'desc',
            },
            {
              header: 'Order Number',
              accessorKey: 'orderNumber',
            },
            {
              header: 'Status',
              id: 'status',
              accessorKey: 'status',
            },
            {
              header: 'Order Value',
              id: 'orderValue',
              accessorFn: (x) => currencyFormat(x.orderValue),
            },
          ]}
          onColumnSorted={filters.handleTableColumnSorted}
          isLoading={cashOrdersListLoading}
          columnTemplate={'1fr 1fr 1fr 1fr'}
          data={cashOrdersList}
          tableVariant='list'
          noDataMessage={cashOrdersListError ? Validation.CONTENT_ERROR : Validation.NO_CONTENT}
          onRowClick={(rowData) => (rowData.status === 'Active' ? setSelectedOrder({ ...rowData }) : undefined)}
          rowClassFn={(rowData) => (rowData.status !== 'Active' ? 'ampol-cash-orders__disabled-table-row' : '')}
        />
      </div>

      <div className='mobile-paginator-control'>
        <Paginator
          itemCount={cashOrdersListResultSize}
          pageNumber={filters.cashOrdersQueryParams.filterValues.pageNumber}
          onPageChange={filters.handlePageNumberChange}
          name={'ampol-cash'}
        />
      </div>
    </>
  );
};

export default AmpolCashOrders;
